import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyFocusBlocksApi, preferencesApi } from '../../api/services';
import type { DailyFocusBlock } from '../../types/models';
import { formatMinutes } from '../../utils/dashboard';
import { focusTimeCopy } from '../../copy/focusTime';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// WeeklyFocusKpi — single-number reinforcement of the focus-time promise on
// the briefing dashboard. Hours protected this week vs target, plus a thin
// proportional bar in the same language as CapacityStrip.
// ---------------------------------------------------------------------------

function startOfWeek(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  return date;
}

function toIsoDate(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function blockMinutes(b: DailyFocusBlock): number {
  const [sh, sm] = b.startTime.split(':').map(Number);
  const [eh, em] = b.endTime.split(':').map(Number);
  return Math.max(0, eh * 60 + em - (sh * 60 + sm));
}

export function WeeklyFocusKpi() {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState<DailyFocusBlock[] | null>(null);
  const [targetDailyMinutes, setTargetDailyMinutes] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);

  const { monday, sunday } = useMemo(() => {
    const now = new Date();
    const m = startOfWeek(now);
    const s = new Date(m);
    s.setDate(s.getDate() + 6);
    return { monday: toIsoDate(m), sunday: toIsoDate(s) };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      dailyFocusBlocksApi.getByRange(monday, sunday).catch(() => null),
      preferencesApi.get().catch(() => null),
    ])
      .then(([bs, prefs]) => {
        if (cancelled) return;
        if (bs === null || prefs === null) {
          setError(true);
          return;
        }
        setBlocks(bs);
        setTargetDailyMinutes(prefs.idealDailyFocusMinutes ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [monday, sunday]);

  const protectedMinutes = useMemo(
    () => (blocks ? blocks.reduce((sum, b) => sum + blockMinutes(b), 0) : 0),
    [blocks],
  );
  const targetWeeklyMinutes = (targetDailyMinutes ?? 0) * 5;
  const ratio = targetWeeklyMinutes > 0 ? Math.min(1, protectedMinutes / targetWeeklyMinutes) : 0;

  // Severity tone. Target >= 100% → on plan; >= 70% → at risk; below → breach.
  // Mapped to the same `status.*` semantic tokens the rest of the dashboard uses;
  // here we use Tailwind classes that resolve via the shared palette in tokens.ts.
  let barClass = 'bg-emerald-500';
  if (targetWeeklyMinutes === 0) {
    barClass = 'bg-amber-500';
  } else if (ratio < 0.7) {
    barClass = 'bg-rose-500';
  } else if (ratio < 1) {
    barClass = 'bg-amber-500';
  }

  const loading = blocks === null && !error;

  const tooltipLabel =
    targetWeeklyMinutes === 0
      ? 'Set an ideal daily focus target in Preferences to calibrate this metric.'
      : `${formatMinutes(protectedMinutes)} protected of ${formatMinutes(targetWeeklyMinutes)} target this week.`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => navigate('/focus')}
            className="block w-full rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary"
          >
            <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {focusTimeCopy.kpiLabel}
            </span>

            {loading ? (
              <div className="mt-0.5 h-9 w-32 animate-pulse rounded bg-muted" />
            ) : (
              <div className="mt-0.5 flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums tracking-tight">
                  {formatMinutes(protectedMinutes)}
                </span>
                {targetWeeklyMinutes > 0 && (
                  <span className="text-sm text-muted-foreground">
                    of {formatMinutes(targetWeeklyMinutes)} {focusTimeCopy.kpiVsTarget}
                  </span>
                )}
              </div>
            )}

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                aria-hidden
                className={cn('h-full transition-[width] duration-200', barClass)}
                style={{ width: `${ratio * 100}%` }}
              />
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltipLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default WeeklyFocusKpi;
